import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, KeyRound } from "lucide-react";
import { api, errorMessage } from "@/lib/api";

type ChildRow = { studentUserId: string; studentName: string };

export default function ParentStudentPassword() {
  const { studentUserId = "" } = useParams();
  const [student, setStudent] = useState<ChildRow | null>(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    let cancel = false;
    setStudent(null);
    setError("");
    api<{ success: boolean; data: ChildRow[] }>("/api/parent/students")
      .then((env) => {
        if (cancel) return;
        const found = (env.data || []).find((row) => row.studentUserId === studentUserId);
        if (found) setStudent(found);
        else setError("That student is not linked to you.");
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [studentUserId]);

  const back = (
    <Link className="student-text-link" to={`/parent/student/${encodeURIComponent(studentUserId)}`}>
      <ChevronLeft size={15} /> {student?.studentName || "Back"}
    </Link>
  );

  if (error) {
    return (
      <div className="student-page student-page--parent">
        {back}
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="student-page student-page--parent" aria-busy="true">
        <span className="student-vh">Loading this student</span>
      </div>
    );
  }

  const mismatch = Boolean(password && confirm && password !== confirm);

  return (
    <div className="student-page student-page--parent">
      {back}
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">{student.studentName}</span>
        <h1>Reset password</h1>
        <p>
          Choose a new password for {student.studentName}. They will use it the next time they sign in.
        </p>
      </div>
      <section className="student-card parent-form-card">
        <form
          className="parent-form"
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <label className="parent-field" htmlFor="parent-reset-password">
            <span>New password</span>
            <input
              id="parent-reset-password"
              className="student-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="parent-field" htmlFor="parent-reset-confirm">
            <span>Confirm new password</span>
            <input
              id="parent-reset-confirm"
              className="student-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              aria-invalid={mismatch}
              aria-describedby={mismatch ? "parent-reset-mismatch" : undefined}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {mismatch ? (
            <p className="parent-form-error" id="parent-reset-mismatch" role="alert">
              The passwords do not match.
            </p>
          ) : null}
          <div className="parent-form-actions">
            <p className="parent-form-soon" id="parent-reset-soon">
              Resetting is not available yet. It is coming soon.
            </p>
            <button type="submit" className="student-btn" disabled aria-describedby="parent-reset-soon">
              <KeyRound size={15} aria-hidden /> Reset password
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
